import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  Sparkles,
  Gem,
  Film,
  CreditCard,
  ChevronRight,
  Star,
  PlayCircle,
  AlertCircle,
  Lightbulb,
  Zap,
  Layers,
  TrendingUp,
  Gift,
  Rocket,
  X,
  Upload,
  Wand2,
  Download,
} from "lucide-react";
import type { User } from "@supabase/supabase-js";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useCredits } from "@/hooks/useCredits";
import { NotificationsBanner } from "@/components/dashboard/NotificationsBanner";

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({
    meta: [
      { title: "ダッシュボード | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: DashboardPage,
});

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

type GenerationRow = {
  id: string;
  status: string;
  quality_tier: string | null;
  created_at: string;
  user_id: string;
  output_video_url: string | null;
  output_image_url: string | null;
};

type ProfileSummary = {
  display_name: string | null;
  plan: string;
};

type SubscriptionSummary = {
  plan: string | null;
  current_period_end: string | null;
};


function getTimeWord(): string {
  const h = new Date().getHours();
  if (h < 4) return "こんばんは";
  if (h < 11) return "おはよう";
  if (h < 18) return "こんにちは";
  return "こんばんは";
}

function getShortSeasonal(): string {
  const now = new Date();
  const m = now.getMonth() + 1;
  const d = now.getDate();
  if (m === 1 && d === 1) return "あけましておめでとうございます";
  if (m === 1 && d <= 3) return "お正月、ゆっくり過ごせていますか";
  if (m === 2 && d === 3) return "今日は節分";
  if (m === 2 && d === 14) return "今日はバレンタイン";
  if (m === 3 && d === 3) return "今日はひな祭り";
  if (m === 3 && d === 14) return "今日はホワイトデー";
  if (m === 5 && d === 5) return "今日はこどもの日";
  if (m === 7 && d === 7) return "七夕の夜に";
  if (m === 10 && d === 31) return "ハッピーハロウィン";
  if (m === 12 && d >= 24 && d <= 25) return "メリークリスマス";
  if (m === 12 && d === 31) return "今年もお疲れさまでした";
  if (m === 12 || m <= 2) return "寒い日が続きますね";
  if (m === 3) return "春の気配を感じますね";
  if (m === 4) return "新生活の季節ですね";
  if (m === 5) return "新緑が気持ちいい季節ですね";
  if (m === 6) return "梅雨入りの季節ですね";
  if (m === 7 || m === 8) return "暑い日が続きますね";
  if (m === 9) return "秋らしくなってきましたね";
  if (m === 10) return "創作日和の季節ですね";
  return "朝晩冷え込みますね";
}

function buildGreeting(
  totalCount: number | null,
  displayName: string,
): { title: ReactNode; subtitle: string } {
  const time = getTimeWord();
  const nameNode = <span className="text-gradient-brand">{displayName}</span>;

  if (totalCount === null) {
    return { title: <>{time}</>, subtitle: getShortSeasonal() };
  }
  if (totalCount === 0) {
    return {
      title: <>はじめまして、{nameNode}さん</>,
      subtitle: "まずは1本、作ってみましょう",
    };
  }
  if (totalCount < 3) {
    return {
      title: <>{time}、{nameNode}さん</>,
      subtitle: "また会えて嬉しいです",
    };
  }
  if (totalCount < 10) {
    return {
      title: <>おかえりなさい</>,
      subtitle: "今日はどんな作品を作りますか",
    };
  }
  if (totalCount < 30) {
    return {
      title: <>{time}</>,
      subtitle: "いつもありがとうございます",
    };
  }
  return { title: <>{time}</>, subtitle: getShortSeasonal() };
}

function formatRelative(iso: string): string {
  const created = new Date(iso).getTime();
  const diffMin = Math.floor((Date.now() - created) / 60000);
  if (diffMin < 1) return "たった今";
  if (diffMin < 60) return `${diffMin}分前`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}時間前`;
  const d = new Date(iso);
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

function formatPlanName(plan: string | null | undefined): string {
  switch (plan) {
    case "free":
    case "trial":
      return "お試しプラン";
    case "light":
      return "Light";
    case "standard":
      return "Standard";
    case "pro":
      return "Pro";
    default:
      return plan ?? "お試しプラン";
  }
}

function formatNextRenewal(iso: string | null): string {
  if (!iso) return "更新日なし";
  const d = new Date(iso);
  return `次回 ${d.getMonth() + 1}/${d.getDate()}`;
}

function statusBadge(status: string) {
  switch (status) {
    case "completed":
    case "succeeded":
      return { label: "完了", className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" };
    case "processing":
    case "running":
      return { label: "生成中", className: "bg-sky-500/15 text-sky-700 dark:text-sky-300" };
    case "pending":
    case "queued":
      return { label: "待機中", className: "bg-amber-500/15 text-amber-700 dark:text-amber-300" };
    case "failed":
    case "error":
      return { label: "失敗", className: "bg-destructive/15 text-destructive" };
    default:
      return { label: status, className: "bg-muted text-muted-foreground" };
  }
}

function thumbnailPath(userId: string, generationId: string): string {
  return `${userId}/${generationId}.jpg`;
}
// SUPABASE_URL retained for potential future use
void SUPABASE_URL;

function DashboardPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<ProfileSummary | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionSummary | null>(null);
  const [monthCount, setMonthCount] = useState<number | null>(null);
  const [recent, setRecent] = useState<GenerationRow[] | null>(null);
  const [thumbUrls, setThumbUrls] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const credits = useCredits(user?.id ?? null);

  // Get current user
  useEffect(() => {
    let active = true;
    supabase.auth.getUser().then(({ data }) => {
      if (!active) return;
      setUser(data.user ?? null);
    });
    return () => {
      active = false;
    };
  }, []);

  // Load all dashboard data
  useEffect(() => {
    if (!user) return;
    let active = true;

    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    (async () => {
      try {
        const [profileRes, subRes, monthRes, recentRes] = await Promise.all([
          supabase
            .from("profiles")
            .select("display_name, plan")
            .eq("user_id", user.id)
            .maybeSingle(),
          supabase
            .from("subscriptions")
            .select("plan, current_period_end")
            .eq("user_id", user.id)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
          supabase
            .from("generations")
            .select("id", { count: "exact", head: true })
            .eq("user_id", user.id)
            .gte("created_at", startOfMonth.toISOString()),
          supabase
            .from("generations")
            .select("id, status, quality_tier, created_at, user_id, output_video_url, output_image_url")
            .eq("user_id", user.id)
            .is("deleted_at", null)
            .not("status", "in", "(failed,error)")
            .order("created_at", { ascending: false })
            .limit(5),
        ]);

        if (!active) return;

        if (profileRes.error) throw profileRes.error;
        if (monthRes.error) throw monthRes.error;
        if (recentRes.error) throw recentRes.error;

        setProfile(profileRes.data ?? { display_name: null, plan: "free" });
        setSubscription(subRes.data ?? null);
        setMonthCount(monthRes.count ?? 0);
        const rows = (recentRes.data ?? []) as GenerationRow[];
        setRecent(rows);

        // Batch-sign thumbnail URLs (private bucket; TTL 1h) for rows that
        // would fall back to the bucket image (no output_image_url/video).
        const needsThumb = rows.filter(
          (r) => !r.output_video_url && !r.output_image_url,
        );
        if (needsThumb.length > 0) {
          const paths = needsThumb.map((r) => thumbnailPath(r.user_id, r.id));
          const { data: signed } = await supabase
            .storage
            .from("thumbnails")
            .createSignedUrls(paths, 3600);
          if (active && signed) {
            const map: Record<string, string> = {};
            signed.forEach((s, idx) => {
              if (s.signedUrl && !s.error) map[needsThumb[idx].id] = s.signedUrl;
            });
            setThumbUrls(map);
          }
        }
      } catch (e) {
        if (!active) return;
        setError(e instanceof Error ? e.message : "データを取得できませんでした");
      }
    })();

    return () => {
      active = false;
    };
  }, [user]);

  const [totalCount, setTotalCount] = useState<number | null>(null);

  // Fetch lifetime generation count for usage-stage greeting
  useEffect(() => {
    if (!user) return;
    let active = true;
    supabase
      .from("generations")
      .select("id", { count: "exact", head: true })
      .eq("user_id", user.id)
      .then(({ count }) => {
        if (active) setTotalCount(count ?? 0);
      });
    return () => {
      active = false;
    };
  }, [user]);

  const displayName = useMemo(() => {
    return profile?.display_name || user?.email?.split("@")[0] || "ゲスト";
  }, [profile, user]);

  const { title: greetingTitle, subtitle: greetingSubtitle } = useMemo(
    () => buildGreeting(totalCount, displayName),
    [totalCount, displayName],
  );
  const planName = formatPlanName(subscription?.plan ?? profile?.plan);
  // renewalLabel removed with compact stat redesign
  void formatNextRenewal;

  return (
    <div className="px-4 py-8 md:px-8 md:py-10">
      <div className="mx-auto max-w-6xl space-y-10">
        {/* Greeting + CTA */}
        <section className="space-y-5">
          {profile === null && !error ? (
            <Skeleton className="h-10 w-72" />
          ) : (
            <div className="space-y-2">
              <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
                {greetingTitle}
              </h1>
              <p className="text-sm text-muted-foreground sm:text-base">{greetingSubtitle}</p>
            </div>
          )}
          <Button
            asChild
            size="lg"
            className="h-14 rounded-full bg-gradient-brand px-8 text-base font-bold text-white shadow-xl shadow-brand-primary/40 transition-transform hover:-translate-y-0.5 hover:opacity-95"
          >
            <Link to="/create">
              <Sparkles className="mr-1.5 h-5 w-5" />
              新規作成
            </Link>
          </Button>
        </section>

        <NotificationsBanner userId={user?.id ?? null} />

        {/* First-time tutorial (dismissible) */}
        {totalCount === 0 && <FirstTimeTutorial />}

        {/* No credits alert */}
        {credits !== null && credits === 0 && <NoCreditsCard />}

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-semibold">データの読み込みに失敗しました</p>
              <p className="opacity-80">{error}</p>
            </div>
          </div>
        )}

        {/* Compact status row */}
        <section className="grid grid-cols-3 gap-2 sm:gap-3">
          <CompactStat
            icon={<Gem className="h-4 w-4" />}
            label="クレジット"
            value={credits === null ? null : `${credits}`}
          />
          <CompactStat
            icon={<Film className="h-4 w-4" />}
            label="今月"
            value={monthCount === null ? null : `${monthCount}本`}
          />
          <CompactStat
            icon={<CreditCard className="h-4 w-4" />}
            label="プラン"
            value={profile === null ? null : planName}
            to="/credits-shop"
          />
        </section>

        {/* Tip of the day */}
        <TipCard />

        {/* Recent generations */}
        <section className="space-y-4">
          <div className="flex items-end justify-between">
            <h2 className="text-xl font-bold tracking-tight">最近作った動画</h2>
            {recent && recent.length > 0 && (
              <Link
                to="/history"
                className="inline-flex items-center text-sm font-medium text-primary hover:underline"
              >
                すべて見る
                <ChevronRight className="h-4 w-4" />
              </Link>
            )}
          </div>

          {recent === null ? (
            <div className="flex gap-4 overflow-hidden">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-56 w-44 shrink-0 rounded-xl" />
              ))}
            </div>
          ) : recent.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center gap-4 py-12 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                  <Film className="h-6 w-6 text-primary" />
                </div>
                <div className="space-y-1">
                  <p className="font-semibold">まだ動画がありません</p>
                  <p className="text-sm text-muted-foreground">
                    最初の動画を作ってみましょう！
                  </p>
                </div>
                <Button asChild className="bg-gradient-brand text-white">
                  <Link to="/create">
                    <Sparkles className="mr-1 h-4 w-4" />
                    新規作成
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="-mx-4 flex gap-4 overflow-x-auto px-4 pb-2 [scrollbar-width:thin]">
              {recent.map((g) => (
                <RecentCard key={g.id} generation={g} thumbUrl={thumbUrls[g.id] ?? null} />
              ))}
            </div>
          )}
        </section>

        {/* Pro tips */}
        <ProTipsSection />

        {/* Combine into one video CTA - hidden until story reel is implemented */}
        {/* <CombineVideosCard /> */}
      </div>
    </div>
  );
}

const TIPS = [
  {
    icon: Lightbulb,
    title: "プロンプトは具体的に",
    body: "「カメラの動き」「光の方向」「表情」を入れると品質が劇的に上がります。",
  },
  {
    icon: Zap,
    title: "高品質モードを試そう",
    body: "重要な1本は高品質モードで生成。SNS映えが段違いです。",
  },
  {
    icon: Star,
    title: "テンプレ活用で時短",
    body: "迷ったらテンプレから。プロが調整した構成で成功率UP。",
  },
];

function TipCard() {
  const tip = useMemo(() => TIPS[Math.floor(Date.now() / 86400000) % TIPS.length], []);
  const Icon = tip.icon;
  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
      <CardContent className="flex items-start gap-3 p-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 space-y-0.5">
          <p className="text-xs font-semibold text-primary">生成のヒント</p>
          <p className="font-bold">{tip.title}</p>
          <p className="text-sm text-muted-foreground">{tip.body}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function ProTipsSection() {
  return (
    <section className="space-y-4">
      <h2 className="text-xl font-bold tracking-tight">高品質に作るコツ</h2>
      <div className="grid gap-3 sm:grid-cols-3">
        {[
          { icon: TrendingUp, title: "縦長9:16で撮影", body: "ショート動画は縦長が伸びやすい" },
          { icon: Sparkles, title: "顔ははっきり明るく", body: "暗い写真は変身精度が落ちます" },
          { icon: Gift, title: "招待で無料クレジット", body: "友達招待で追加クレジット獲得" },
        ].map((t) => (
          <Card key={t.title} className="transition-all hover:-translate-y-0.5 hover:shadow-md">
            <CardContent className="space-y-1.5 p-4">
              <t.icon className="h-5 w-5 text-primary" />
              <p className="font-semibold">{t.title}</p>
              <p className="text-xs text-muted-foreground">{t.body}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}

function CombineVideosCard() {
  return (
    <Card className="overflow-hidden border-primary/30 bg-gradient-to-r from-primary/10 via-accent/10 to-transparent">
      <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15">
            <Layers className="h-5 w-5 text-primary" />
          </div>
          <div className="space-y-0.5">
            <p className="font-bold">複数動画をつなげて1本に</p>
            <p className="text-sm text-muted-foreground">
              生成した動画を結合してストーリー仕立てのリールに。
            </p>
          </div>
        </div>
        <Button asChild variant="outline" className="sm:shrink-0">
          <Link to="/history">
            履歴から選ぶ
            <ChevronRight className="ml-1 h-4 w-4" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

interface CompactStatProps {
  icon: React.ReactNode;
  label: string;
  value: string | null;
  onClick?: () => void;
  to?: string;
}

function CompactStat({ icon, label, value, onClick, to }: CompactStatProps) {
  const interactive = !!onClick || !!to;
  const className = cn(
    "flex flex-col items-start gap-1 rounded-xl border border-border bg-card p-3 text-left transition-all",
    interactive && "cursor-pointer hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-md",
    !interactive && "cursor-default",
  );
  const inner = (
    <>
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
        <span className="text-primary">{icon}</span>
        {label}
      </div>
      {value === null ? (
        <Skeleton className="h-5 w-12" />
      ) : (
        <p className="truncate text-base font-bold tracking-tight sm:text-lg">{value}</p>
      )}
    </>
  );
  if (to) {
    return (
      <Link to={to} className={className}>
        {inner}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      className={className}
    >
      {inner}
    </button>
  );
}


interface RecentCardProps {
  generation: GenerationRow;
  thumbUrl: string | null;
}

function RecentCard({ generation, thumbUrl }: RecentCardProps) {
  const [mediaFailed, setMediaFailed] = useState(false);
  const badge = statusBadge(generation.status);
  const videoUrl = generation.output_video_url;
  const imageUrl = generation.output_image_url ?? thumbUrl;

  return (
    <Link
      to="/result/$id"
      params={{ id: generation.id }}
      className="group block w-44 shrink-0 overflow-hidden rounded-xl border border-border bg-card transition-all hover:-translate-y-1 hover:shadow-lg"
    >
      <div className="relative aspect-[9/16] overflow-hidden bg-muted">
        {mediaFailed ? (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-muted to-secondary">
            <PlayCircle className="h-10 w-10 text-muted-foreground/50" />
          </div>
        ) : videoUrl ? (
          <video
            src={`${videoUrl}#t=0.1`}
            muted
            playsInline
            preload="metadata"
            onError={() => setMediaFailed(true)}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
          />
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            loading="lazy"
            onError={() => setMediaFailed(true)}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-muted to-secondary">
            <PlayCircle className="h-10 w-10 text-muted-foreground/50" />
          </div>
        )}
        <Badge
          variant="secondary"
          className={cn("absolute left-2 top-2 border-0 backdrop-blur", badge.className)}
        >
          {badge.label}
        </Badge>
      </div>
      <div className="space-y-1 p-2.5">
        <p className="truncate text-sm font-semibold">
          {generation.quality_tier ?? "標準品質"}
        </p>
        <p className="text-xs text-muted-foreground">
          {formatRelative(generation.created_at)}
        </p>
      </div>
    </Link>
  );
}

function NoCreditsCard() {
  return (
    <Card className="border-amber-500/40 bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-transparent">
      <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-500/20">
            <Gem className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="space-y-0.5">
            <p className="font-bold">クレジットがありません</p>
            <p className="text-sm text-muted-foreground">
              動画や画像を生成するにはクレジットが必要です。プランを購入して始めましょう。
            </p>
          </div>
        </div>
        <Button
          asChild
          className="bg-gradient-brand text-white shadow-lg shadow-brand-primary/30 sm:shrink-0"
        >
          <Link to="/credits-shop">
            <CreditCard className="mr-1 h-4 w-4" />
            クレジットを購入
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

const TUTORIAL_KEY = "kagem_tutorial_dismissed_v1";

const TUTORIAL_STEPS = [
  {
    icon: CreditCard,
    title: "クレジットを購入",
    body: "まずはプランを選んでクレジットをチャージ。用途に合わせて選べます。",
  },
  {
    icon: Upload,
    title: "素材をアップロード",
    body: "変身したい顔写真と、動きの元になる動画（または画像）を用意します。",
  },
  {
    icon: Wand2,
    title: "生成をスタート",
    body: "テンプレートやモードを選んで、あとはAIにお任せ。数十秒〜数分で完成。",
  },
  {
    icon: Download,
    title: "保存・シェア",
    body: "できあがった動画・画像はダウンロードしてSNSでシェアできます。",
  },
];

function FirstTimeTutorial() {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(TUTORIAL_KEY) === "1";
  });

  if (dismissed) return null;

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(TUTORIAL_KEY, "1");
    } catch {
      // ignore
    }
    setDismissed(true);
  };

  return (
    <Card className="relative overflow-hidden border-primary/30 bg-gradient-to-br from-primary/10 via-accent/5 to-transparent">
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="チュートリアルを閉じる"
        className="absolute right-3 top-3 rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
      <CardContent className="space-y-5 p-5 sm:p-6">
        <div className="flex items-start gap-3 pr-8">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15">
            <Rocket className="h-5 w-5 text-primary" />
          </div>
          <div className="space-y-0.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">
              はじめての方へ
            </p>
            <p className="text-lg font-bold">KAGEMの使い方（4ステップ）</p>
            <p className="text-sm text-muted-foreground">
              最初の1本を作るまでの流れをご紹介します。
            </p>
          </div>
        </div>

        <ol className="grid gap-3 sm:grid-cols-2">
          {TUTORIAL_STEPS.map((step, idx) => (
            <li
              key={step.title}
              className="flex items-start gap-3 rounded-lg border border-border/60 bg-card/60 p-3"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                {idx + 1}
              </div>
              <div className="min-w-0 space-y-0.5">
                <div className="flex items-center gap-1.5">
                  <step.icon className="h-3.5 w-3.5 text-primary" />
                  <p className="text-sm font-semibold">{step.title}</p>
                </div>
                <p className="text-xs text-muted-foreground">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="flex flex-wrap gap-2">
          <Button
            asChild
            className="bg-gradient-brand text-white shadow-md shadow-brand-primary/30"
          >
            <Link to="/credits-shop">
              <CreditCard className="mr-1 h-4 w-4" />
              クレジットを購入
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/how-to-use">
              詳しい使い方を見る
              <ChevronRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
